import { elide } from "@common/utils/activationSuiteDisplay"

/**
 * Les formats de cours du funnel : ce que dit chaque tuile, et ce que montre
 * « Une heure de {prénom} » (le volet du « i », la page du format).
 *
 * Un format se choisit sur ce que l'élève vit de son heure : le temps passé
 * avec le masteur, et le temps d'entraînement. Le nombre d'élèves n'est plus
 * l'argument ; il se dit une fois, dans le détail.
 *
 * `offerType` est l'offre que le serveur vend sous ce format. Les noms ne
 * valent que pour ce funnel : le reste de l'app dit encore « Cours partagé »
 * et « Cours particulier ».
 *
 * `share` est le sous-titre du format, sur sa tuile : la seule phrase qui le
 * résume. `hour` est la phrase qui se lit sous les deux temps, quand le
 * format en demande une : `text`, puis `note`, ce qu'un parent demande
 * d'abord du format.
 *
 * Une heure dure 60 minutes. `visits` liste les temps où le masteur parle à
 * l'élève, [début, fin] ; le reste, l'élève s'entraîne. `beside` : le masteur
 * reste avec lui pendant l'entraînement. Ces durées sont un exemple, pas une
 * promesse de partage.
 */

/** Ordre des tuiles et des onglets : le format le plus choisi d'abord. */
export const FORMAT_KEYS = ["tandem", "solo"]

/**
 * Où le format se choisit, tiré au sort par le serveur à la création du
 * dossier : sur la page de la formule (`samePage`, l'existant) ou sur sa
 * propre page, juste après (`ownStep`). Les deux se vendent ; le serveur
 * compte ce que chacune convertit.
 */
export const FORMAT_STEP = { samePage: "same_page", ownStep: "own_step" }

export const INSCRIPTION_FORMATS = {
    solo: {
        offerType: "integral",
        name: "Solo",
        share: "Tuteur dédié sur toute l'heure",
        training: (firstname) =>
            `${firstname} met en pratique, le masteur à ses côtés.`,
        beside: true,
        // Une barre pleine : le masteur est là toute l'heure, rien n'alterne.
        visits: [[0, 60]],
        alt: (firstname) =>
            `Une heure ${elide(firstname)} : les temps avec le masteur et les temps d'entraînement se succèdent, et le masteur reste présent du début à la fin.`,
    },
    tandem: {
        offerType: "essential",
        name: "Tandem",
        share: "Le bon rythme entre explications et pratique",
        popular: true,
        training: (firstname) =>
            `${firstname} met en pratique, pendant un temps court.`,
        // Le masteur suit un autre élève pendant l'entraînement : le parent
        // demande d'abord s'ils se voient. La phrase le dit, là où il regarde.
        hour: {
            text: "Pendant l'entraînement, le masteur accompagne un autre élève.",
            note: "Ils ne se voient pas.",
        },
        // La moitié de l'heure au plus : deux élèves se partagent le masteur.
        visits: [
            [0, 8],
            [15, 23],
            [30, 38],
            [45, 51],
        ],
        alt: (firstname) =>
            `Une heure ${elide(firstname)} : les temps avec le masteur alternent avec les temps d'entraînement sur son programme.`,
    },
}

/** L'offre d'un dossier qui n'a pas encore choisi : celle du format le plus choisi. */
export const DEFAULT_OFFER_TYPE = INSCRIPTION_FORMATS.tandem.offerType

/** Le format que le serveur vend sous cette offre — `null` s'il n'en connaît pas. */
export const formatOfOffer = (offerType) =>
    FORMAT_KEYS.map((key) => INSCRIPTION_FORMATS[key]).find(
        (format) => format.offerType === offerType
    ) ?? null

/** « Masteur Tandem » ; à défaut de format connu, « Masteur » seul. */
export const formatNameOfOffer = (offerType) => {
    const format = formatOfOffer(offerType)
    return format ? `Masteur ${format.name}` : "Masteur"
}

// Les mots de l'engagement, écrits une fois : l'interrupteur, le pied de page
// et la ligne « Engagement » de la réservation les reprennent tous d'ici.
const COMMITMENT_DURATION = "12\u00a0mois"
const NO_COMMITMENT = "Sans engagement"

export const COMMITMENT_COPY = {
    duration: COMMITMENT_DURATION,
    none: NO_COMMITMENT,
    label: `Engagement de\u00a0${COMMITMENT_DURATION}`,
    // Sur l'interrupteur, rien que la durée : l'argument est l'économie, dite
    // juste dessous. Le premier mois sans engagement se dit à la réservation,
    // dans les garanties.
    switchLabel: `Engagement ${COMMITMENT_DURATION}`,
    // Ce que l'engagement fait gagner (`amount` : « 840 € », lu sur la grille
    // du serveur).
    saving: (amount) => `Économisez ${amount} sur une année`,
    firstMonth: "Annulable sans frais le 1er\u00a0mois",
    footerNoCommitment: `${NO_COMMITMENT}, résiliable chaque mois`,
    lockedTile: "Avec engagement",
}

export const START_OPTION_TITLES = {
    earliest: "Au plus tôt",
    next_month: "Le mois prochain",
}

export const FORMAT_INFO_COPY = {
    title: (firstname) => `Une heure ${elide(firstname)}`,
    // Sur la page du format, sans onglets : le titre nomme le format ouvert.
    titleIn: (firstname, format) =>
        `Une heure ${elide(firstname)} en mode ${format}`,
    withMasteur: {
        title: "Avec son tuteur",
        text: "Il explique et corrige, en face à face.",
    },
    training: "En entraînement",
    close: "Compris",
}
